/**
 * How long a call's recording is kept, and the legal hold that keeps it past that (`platform`
 * ADR-090, ADR-094), as the call's details say it.
 *
 * Three states, and the row under the transcript says exactly one of them:
 *
 *   on hold   nothing deletes the recording or the call's transcripts until an admin releases it
 *   dated     `expires_at` is when the retention sweep deletes them
 *   kept      no retention window applies to this call's domain, so nothing is scheduled
 *
 * The reason for a hold, and who placed it, come from the server for an admin and above only: a
 * reason may name a legal matter, and a rep needs to know that the call is kept, not why.
 */

export const MIN_HOLD_REASON = 3;
export const MAX_HOLD_REASON = 500;

export interface Keeping {
  on_hold: boolean;
  held_at: string | null;
  hold_reason: string | null;
  held_by_name: string | null;
  expires_at: string | null;
}

export type KeepingState = "on-hold" | "dated" | "kept";

export function keepingState(recording: Pick<Keeping, "on_hold" | "expires_at">): KeepingState {
  if (recording.on_hold) return "on-hold";
  return recording.expires_at ? "dated" : "kept";
}

/** A day as the row says it: "Oct 31, 2026". Dates here are days, not moments. */
export function day(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * The row's main line. A date that has already passed is not written as a date: the call is past
 * its window and the next sweep takes it, which is what a reader who has just released a hold
 * needs to be told.
 */
export function keepingLabel(
  recording: Pick<Keeping, "on_hold" | "expires_at" | "held_at">,
  now: number = Date.now(),
): string {
  switch (keepingState(recording)) {
    case "on-hold":
      return recording.held_at ? `On legal hold since ${day(recording.held_at)}` : "On legal hold";
    case "dated":
      return new Date(recording.expires_at as string).getTime() <= now
        ? "Past its retention window: deleted within the hour"
        : `Deleted after ${day(recording.expires_at)}`;
    default:
      return "Kept: no retention window";
  }
}

/** Who placed the hold and why, for a reader the server told. Empty for anyone else. */
export function holdDetail(recording: Pick<Keeping, "hold_reason" | "held_by_name">): string {
  return [recording.hold_reason, recording.held_by_name && `placed by ${recording.held_by_name}`]
    .filter(Boolean)
    .join(" · ");
}

/** What is wrong with a reason, in words, or undefined when it can be saved. */
export function holdReasonError(text: string): string | undefined {
  const reason = text.trim();
  if (reason.length < MIN_HOLD_REASON) return "Say why this call has to be kept.";
  if (reason.length > MAX_HOLD_REASON) return `Keep it under ${MAX_HOLD_REASON} characters.`;
  return undefined;
}
